/**
 * @file useAuth.ts
 * @description Custom React hook for authentication state management. Provides session,
 * user, loading state, and auth action methods (signIn, signUp, signOut).
 *
 * How it works:
 * - On mount, fetches the current session from Supabase Auth
 * - Subscribes to onAuthStateChange for real-time session updates
 * - Exposes signIn (email/password), signUp (with optional business name), and signOut
 * - Returns { session, user, loading, signIn, signUp, signOut }
 *
 * @changelog
 * - 2026-03-19: Added documentation header
 */
import { useState, useEffect, useCallback } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@shared/lib/supabase';

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    session: null,
    user: null,
    loading: true,
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setState({ session, user: session?.user ?? null, loading: false });
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setState({ session, user: session?.user ?? null, loading: false });
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async (email: string, password: string, businessName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { business_name: businessName },
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  return { ...state, signIn, signUp, signOut };
}
